import React, { useEffect, useState } from "react";
import {
  Space,
  Row,
  Col,
  Card,
  Empty,
  Flex,
  Typography,
  Progress,
  Tooltip,
  Dropdown,
  Badge,
  Button,
  Input,
  theme
} from "antd";
import {
  ProjectOutlined,
  EllipsisOutlined,
  EyeOutlined,
  BlockOutlined,
  CheckCircleOutlined,
  OrderedListOutlined,
  SearchOutlined
} from "@ant-design/icons";
import axios from "axios";
import { useHistory, useLocation } from "react-router-dom";
import AddBoard from "./AddBoard";
import UpdateBoard from "./UpdateBoard";
import DeleteBoardModal from "./DeleteBoardModal";
import "./boarding.css";
import "./ticket.css";
import { Error } from "../../../commons/Messages/Messages";
import PageToolbar from "../../../design-system/PageToolbar";

import { useTranslation } from "react-i18next";
const { Title, Text } = Typography;

// Defined outside the page: as a nested component it was a new component type on every render,
// so each keystroke in the search remounted every card and closed any open rename/delete dialog.
const BoardCard = ({ board, onOpen, updateParent }) => {
  const { t } = useTranslation("dashboard");
  const { token } = theme.useToken();
  const [menuOpen, setMenuOpen] = useState(false);
  const progress =
    board.taskCount > 0 ? Math.round((board.finishedTasks / board.taskCount) * 100) : 0;

  const menu = (
    <div className="tkt-dropdown-menu">
      <UpdateBoard data={board} updateParent={updateParent} onOpen={() => setMenuOpen(false)} />
      <DeleteBoardModal
        data={board}
        updateParent={updateParent}
        onOpen={() => setMenuOpen(false)}
      />
    </div>
  );

  return (
    <Col xs={24} sm={12} lg={8} xl={6}>
      <Card
        hoverable
        className="board-card"
        cover={
          <div className="board-card__cover" onClick={() => onOpen(board._id)}>
            <div className="board-card__icon">
              <ProjectOutlined />
            </div>
            <div className="board-card__pattern" />
          </div>
        }
        actions={[
          <Tooltip key="view" title={t("View Detailed Board")}>
            <Button
              type="text"
              icon={<EyeOutlined />}
              aria-label={t("View Detailed Board")}
              onClick={() => onOpen(board._id)}
            />
          </Tooltip>,
          <Dropdown
            key="menu"
            dropdownRender={() => menu}
            trigger={["click"]}
            placement="bottomRight"
            open={menuOpen}
            onOpenChange={setMenuOpen}
          >
            <Button type="text" icon={<EllipsisOutlined />} aria-label={t("Actions")} />
          </Dropdown>
        ]}
      >
        <div className="board-card__content" onClick={() => onOpen(board._id)}>
          <div className="board-card__title-row">
            <Title level={4} className="board-card__title">
              {board.title}
            </Title>
            <Badge
              count={board.groupCount}
              style={{ backgroundColor: token.colorPrimary }}
              title={t("Ticket Groups")}
            />
          </div>

          <Flex vertical gap={12} className="board-card__stats">
            <div className="board-card__stat">
              <Space>
                <Text type="secondary">
                  <BlockOutlined />
                </Text>
                <Text type="secondary">{t("Tickets")}</Text>
              </Space>
              <Text strong>{board.ticketCount}</Text>
            </div>
            <div className="board-card__stat">
              <Space>
                <Text type="secondary">
                  <OrderedListOutlined />
                </Text>
                <Text type="secondary">{t("Total Tasks")}</Text>
              </Space>
              <Text strong>{board.taskCount}</Text>
            </div>

            <div className="board-card__progress">
              <Flex justify="space-between" align="center">
                <Space>
                  <CheckCircleOutlined style={{ color: token.colorSuccess }} />
                  <Text type="secondary" className="board-card__small">
                    {t("Completion")}
                  </Text>
                </Space>
                <Text strong className="board-card__small">
                  {progress}%
                </Text>
              </Flex>
              <Progress
                percent={progress}
                size="small"
                status="active"
                strokeColor={token.colorPrimary}
                showInfo={false}
              />
            </div>
          </Flex>
        </div>
      </Card>
    </Col>
  );
};

const Boardingtickets = () => {
  const { t } = useTranslation("dashboard");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const History = useHistory();
  const { pathname } = useLocation();
  // The page is mounted under /user and /client; open boards under the same prefix.
  const boardPath = boardId => `/${pathname.split("/")[1]}/tickets/${boardId}`;
  const [searchText, setSearchText] = useState("");

  // Only the first load shows skeletons; refreshes after an edit keep the cards on screen.
  const getData = async () => {
    try {
      if (!data.length) setLoading(true);
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/ticketBoards`, {
        headers: { Authorization: "Bearer " + localStorage.getItem("token") }
      });
      setData(res.data || []);
    } catch (err) {
      Error(err.response?.data?.errorMessage || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  const filteredData = data.filter(item =>
    item.title?.toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <div className="boards-page">
      <div className="boards-page__header">
        <Title level={1} className="boards-page__title">
          {t("Workspaces")}
        </Title>
        <Text type="secondary">{t("Manage your specialized ticket boards")}</Text>
      </div>

      <PageToolbar
        filters={
          <Input
            prefix={<SearchOutlined />}
            placeholder={t("Search Boards")}
            aria-label={t("Search Boards")}
            onChange={e => setSearchText(e.target.value)}
            allowClear
          />
        }
        actions={<AddBoard updateParent={getData} />}
      />

      {loading ? (
        <Row gutter={[16, 16]}>
          {[1, 2, 3, 4].map(i => (
            <Col xs={24} sm={12} lg={8} xl={6} key={i}>
              <Card loading />
            </Col>
          ))}
        </Row>
      ) : filteredData.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={<Text type="secondary">{t("No Boards Found")}</Text>}
          className="boards-page__empty"
        >
          <AddBoard updateParent={getData} />
        </Empty>
      ) : (
        <Row gutter={[16, 16]}>
          {filteredData.map(board => (
            <BoardCard
              key={board._id}
              board={board}
              updateParent={getData}
              onOpen={id => History.push(boardPath(id))}
            />
          ))}
        </Row>
      )}
    </div>
  );
};

export default Boardingtickets;
